import axios from '@/lib/axios'
import {
  buildAdminRequestConfig,
  type AdminQueryRoot,
  type OrganizationId,
} from '@/lib/organization-scoping'
import type { SalesOrderIdInput } from '@/lib/services/sales-orders/sales-orders.types'
import type {
  SnapdragonAutoPrintSettingsInput,
  SnapdragonAutoPrintSettingsResponse,
  SnapdragonClaimDirectPrintJobResponse,
  SnapdragonDirectPrintJobsResponse,
  SnapdragonDisconnectComputerResponse,
  SnapdragonDismissPrintJobResponse,
  SnapdragonFinishDirectPrintJobResponse,
  SnapdragonFinishDirectPrintJobStatus,
  SnapdragonHealth,
  SnapdragonInstallerCatalog,
  SnapdragonJobFilters,
  SnapdragonPrintAttemptsResponse,
  SnapdragonPrintersResponse,
  SnapdragonPrintJobResponse,
  SnapdragonPrintJobsResponse,
  SnapdragonRealtimeSessionResponse,
  SnapdragonRealtimeSnapshotResponse,
  SnapdragonRefreshPrintersResponse,
  SnapdragonRemoveStalePrintersResponse,
  SnapdragonRetryDirectPrintJobResponse,
  SnapdragonSaveAutoPrintSettingsResponse,
  SnapdragonSetupCodeResponse,
  SnapdragonTestPrintResponse,
  SnapdragonTestPrintVariables,
  SnapdragonWorkstationAssociationInput,
  SnapdragonWorkstationInput,
  SnapdragonWorkstationPrintingSettingsInput,
  SnapdragonWorkstationPrintingSettingsResponse,
  SnapdragonWorkstationResponse,
  SnapdragonWorkstationsResponse,
} from './snapdragon.types'

/** Every Snapdragon endpoint is organization-scoped; `buildAdminRequestConfig()` throws without an id. */
export interface SnapdragonOrganizationScope {
  organizationId: OrganizationId
}

/** The organization a query key belongs to; the scoped key builders always set it. */
export type SnapdragonKeyEntries = SnapdragonOrganizationScope

export interface SnapdragonFilteredKeyEntries extends SnapdragonKeyEntries {
  filters?: SnapdragonJobFilters
}

/** A job query's key; `useSnapdragonJob()` leaves it idle until it has a job id. */
export interface SnapdragonJobKeyEntries extends SnapdragonKeyEntries {
  jobId?: string | null
}

/** Query functions read only the key and the abort signal. */
interface SnapdragonQueryContext<TKeyEntries> {
  queryKey: [AdminQueryRoot & TKeyEntries]
  signal?: AbortSignal
}

/** Calls that act on one Snapdragon print job or local print attempt. */
export interface SnapdragonJobTarget extends SnapdragonOrganizationScope {
  jobId: string
}

/** `SnapdragonController::validatedMachineId()`: the desktop app's machine id, when known. */
interface SnapdragonMachineScope extends SnapdragonOrganizationScope {
  machineId?: string | null
}

/**
 * `SnapdragonDirectPrintJobController::context()` finds the workstation by id plus the browser's
 * computer id or the desktop's machine id.
 */
export type SnapdragonDirectPrintIdentity = SnapdragonOrganizationScope & {
  workstationId: number
} & (
    | { computerId: string; machineId?: string | null }
    | { computerId?: string | null; machineId: string }
  )

export type SnapdragonDirectPrintJobTarget = SnapdragonDirectPrintIdentity & { jobId: number }

export async function getSnapdragonPrinters({
  queryKey: [{ organizationId }],
  signal,
}: SnapdragonQueryContext<SnapdragonKeyEntries>): Promise<SnapdragonPrintersResponse> {
  return (
    (await axios.get<SnapdragonPrintersResponse>(
      '/snapdragon/printers',
      buildAdminRequestConfig({
        organizationId,
        ...(signal ? { signal } : {}),
      })
    )) || { connection: null, printers: [] }
  )
}

export async function getSnapdragonHealth({
  queryKey: [{ organizationId }],
  signal,
}: SnapdragonQueryContext<SnapdragonKeyEntries>): Promise<SnapdragonHealth> {
  return axios.get<SnapdragonHealth>(
    '/snapdragon/health',
    buildAdminRequestConfig({
      organizationId,
      ...(signal ? { signal } : {}),
    })
  )
}

export async function createSnapdragonRealtimeSession({
  organizationId,
}: SnapdragonOrganizationScope): Promise<SnapdragonRealtimeSessionResponse> {
  return axios.post<SnapdragonRealtimeSessionResponse>(
    '/snapdragon/realtime-session',
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getSnapdragonRealtimeSnapshot({
  organizationId,
}: SnapdragonOrganizationScope): Promise<SnapdragonRealtimeSnapshotResponse> {
  return axios.get<SnapdragonRealtimeSnapshotResponse>(
    '/snapdragon/realtime-snapshot',
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getSnapdragonInstallers({
  queryKey: [{ organizationId }],
  signal,
}: SnapdragonQueryContext<SnapdragonKeyEntries>): Promise<SnapdragonInstallerCatalog> {
  return axios.get<SnapdragonInstallerCatalog>(
    '/snapdragon/installers',
    buildAdminRequestConfig({
      organizationId,
      ...(signal ? { signal } : {}),
    })
  )
}

export async function connectSnapdragon({
  organizationId,
  machineId,
}: SnapdragonMachineScope): Promise<SnapdragonSetupCodeResponse> {
  return axios.post<SnapdragonSetupCodeResponse>(
    '/snapdragon/connect',
    machineId ? { machine_id: machineId } : null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function createSnapdragonSetupCode({
  organizationId,
  machineId,
}: SnapdragonMachineScope): Promise<SnapdragonSetupCodeResponse> {
  return axios.post<SnapdragonSetupCodeResponse>(
    '/snapdragon/setup-code',
    machineId ? { machine_id: machineId } : null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function disconnectSnapdragon({
  organizationId,
}: SnapdragonOrganizationScope): Promise<{ connection: null; printers: [] }> {
  return axios.delete<{ connection: null; printers: [] }>(
    '/snapdragon/connection',
    buildAdminRequestConfig({ organizationId })
  )
}

export async function disconnectSnapdragonComputer({
  organizationId,
  stationId,
}: SnapdragonOrganizationScope & {
  stationId: string
}): Promise<SnapdragonDisconnectComputerResponse> {
  return axios.delete<SnapdragonDisconnectComputerResponse>(
    `/snapdragon/computers/${encodeURIComponent(String(stationId))}`,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getSnapdragonWorkstations({
  queryKey: [{ organizationId }],
  signal,
}: SnapdragonQueryContext<SnapdragonKeyEntries>): Promise<SnapdragonWorkstationsResponse> {
  return (
    (await axios.get<SnapdragonWorkstationsResponse>(
      '/snapdragon/workstations',
      buildAdminRequestConfig({
        organizationId,
        ...(signal ? { signal } : {}),
      })
    )) || { workstations: [] }
  )
}

export function saveSnapdragonWorkstationPrintingSettings({
  organizationId,
  workstationId,
  ...settings
}: SnapdragonOrganizationScope &
  SnapdragonWorkstationPrintingSettingsInput & {
    workstationId: number
  }): Promise<SnapdragonWorkstationPrintingSettingsResponse> {
  return axios.patch<SnapdragonWorkstationPrintingSettingsResponse>(
    `/snapdragon/workstations/${workstationId}/printing-settings`,
    settings,
    buildAdminRequestConfig({ organizationId })
  )
}

/** `SnapdragonWorkstationController::show()` finds this browser's workstation by its computer id. */
export interface SnapdragonWorkstationLookup extends SnapdragonOrganizationScope {
  computerId: string
  machineId?: string | null
}

export async function getSnapdragonWorkstation({
  organizationId,
  computerId,
  machineId,
  signal,
}: SnapdragonWorkstationLookup & {
  signal?: AbortSignal
}): Promise<SnapdragonWorkstationResponse> {
  return axios.get<SnapdragonWorkstationResponse>(
    '/snapdragon/workstation',
    buildAdminRequestConfig({
      organizationId,
      params: {
        computer_id: computerId,
        ...(machineId ? { machine_id: machineId } : {}),
      },
      ...(signal ? { signal } : {}),
    })
  )
}

export async function saveSnapdragonWorkstation({
  organizationId,
  ...workstation
}: SnapdragonOrganizationScope &
  SnapdragonWorkstationInput): Promise<SnapdragonWorkstationResponse> {
  return axios.put<SnapdragonWorkstationResponse>(
    '/snapdragon/workstation',
    workstation,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function associateSnapdragonWorkstation({
  organizationId,
  ...identity
}: SnapdragonOrganizationScope &
  SnapdragonWorkstationAssociationInput): Promise<SnapdragonWorkstationResponse> {
  return axios.post<SnapdragonWorkstationResponse>(
    '/snapdragon/workstation/association',
    identity,
    buildAdminRequestConfig({ organizationId })
  )
}

function directPrintJobConfig(
  { organizationId, workstationId, computerId, machineId }: SnapdragonDirectPrintIdentity,
  params: Record<string, unknown> = {}
) {
  return buildAdminRequestConfig({
    organizationId,
    params: {
      workstation_id: workstationId,
      computer_id: computerId,
      ...(machineId ? { machine_id: machineId } : {}),
      ...params,
    },
  })
}

export async function getDirectPrintJobs({
  salesOrderId,
  ...identity
}: SnapdragonDirectPrintIdentity & {
  salesOrderId?: SalesOrderIdInput | null
}): Promise<SnapdragonDirectPrintJobsResponse> {
  return axios.get<SnapdragonDirectPrintJobsResponse>(
    '/snapdragon/direct-print-jobs',
    directPrintJobConfig(identity, salesOrderId ? { sales_order_id: salesOrderId } : {})
  )
}

export async function claimDirectPrintJob({
  jobId,
  ...identity
}: SnapdragonDirectPrintJobTarget): Promise<SnapdragonClaimDirectPrintJobResponse> {
  return axios.post<SnapdragonClaimDirectPrintJobResponse>(
    `/snapdragon/direct-print-jobs/${encodeURIComponent(String(jobId))}/claim`,
    null,
    directPrintJobConfig(identity)
  )
}

export async function finishDirectPrintJob({
  jobId,
  claimAttempt,
  status,
  snapdragonJobId,
  lastError,
  ...identity
}: SnapdragonDirectPrintJobTarget & {
  /** The claim this browser holds; jobs claimed before claims were numbered leave it out. */
  claimAttempt?: number
  status: SnapdragonFinishDirectPrintJobStatus
  snapdragonJobId?: string | null
  lastError?: string | null
}): Promise<SnapdragonFinishDirectPrintJobResponse> {
  return axios.post<SnapdragonFinishDirectPrintJobResponse>(
    `/snapdragon/direct-print-jobs/${encodeURIComponent(String(jobId))}/finish`,
    {
      claim_attempt: claimAttempt,
      status,
      snapdragon_job_id: snapdragonJobId || null,
      last_error: lastError || null,
    },
    directPrintJobConfig(identity)
  )
}

export async function retryDirectPrintJob({
  jobId,
  ...identity
}: SnapdragonDirectPrintJobTarget): Promise<SnapdragonRetryDirectPrintJobResponse> {
  return axios.post<SnapdragonRetryDirectPrintJobResponse>(
    `/snapdragon/direct-print-jobs/${encodeURIComponent(String(jobId))}/retry`,
    null,
    directPrintJobConfig(identity)
  )
}

export async function refreshSnapdragonPrinters({
  organizationId,
}: SnapdragonOrganizationScope): Promise<SnapdragonRefreshPrintersResponse> {
  return axios.post<SnapdragonRefreshPrintersResponse>(
    '/snapdragon/printers/refresh',
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function removeStaleSnapdragonPrinters({
  organizationId,
}: SnapdragonOrganizationScope): Promise<SnapdragonRemoveStalePrintersResponse> {
  return axios.post<SnapdragonRemoveStalePrintersResponse>(
    '/snapdragon/printers/remove-stale',
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getSnapdragonSalesOrderAutoPrintSettings({
  queryKey: [{ organizationId }],
  signal,
}: SnapdragonQueryContext<SnapdragonKeyEntries>): Promise<SnapdragonAutoPrintSettingsResponse> {
  return axios.get<SnapdragonAutoPrintSettingsResponse>(
    '/snapdragon/settings/sales-order-auto-print',
    buildAdminRequestConfig({
      organizationId,
      ...(signal ? { signal } : {}),
    })
  )
}

export async function saveSnapdragonSalesOrderAutoPrintSettings({
  organizationId,
  settings,
}: SnapdragonOrganizationScope & {
  settings: SnapdragonAutoPrintSettingsInput
}): Promise<SnapdragonSaveAutoPrintSettingsResponse> {
  return axios.put<SnapdragonSaveAutoPrintSettingsResponse>(
    '/snapdragon/settings/sales-order-auto-print',
    settings,
    buildAdminRequestConfig({ organizationId })
  )
}

/** Callers refresh print activity and health instead of reading the response. */
export async function sendSnapdragonTestPrint({
  organizationId,
  printerId,
  template,
  tray,
  rotate180,
}: SnapdragonOrganizationScope &
  SnapdragonTestPrintVariables): Promise<SnapdragonTestPrintResponse> {
  return axios.post<SnapdragonTestPrintResponse>(
    '/snapdragon/test-print',
    {
      printer_id: printerId || null,
      ...(template ? { template } : {}),
      tray: tray || null,
      ...(rotate180 !== undefined ? { rotate_180: rotate180 === true } : {}),
    },
    buildAdminRequestConfig({ organizationId })
  )
}

export async function getSnapdragonJobs({
  queryKey: [{ organizationId, filters = {} }],
  signal,
}: SnapdragonQueryContext<SnapdragonFilteredKeyEntries>): Promise<SnapdragonPrintJobsResponse> {
  return (
    (await axios.get<SnapdragonPrintJobsResponse>(
      '/snapdragon/jobs',
      buildAdminRequestConfig({
        organizationId,
        params: filters,
        ...(signal ? { signal } : {}),
      })
    )) || { connection: null, jobs: [], meta: null, links: null }
  )
}

export async function getSnapdragonPrintAttempts({
  queryKey: [{ organizationId, filters = {} }],
  signal,
}: SnapdragonQueryContext<SnapdragonFilteredKeyEntries>): Promise<SnapdragonPrintAttemptsResponse> {
  return (
    (await axios.get<SnapdragonPrintAttemptsResponse>(
      '/snapdragon/print-attempts',
      buildAdminRequestConfig({
        organizationId,
        params: filters,
        ...(signal ? { signal } : {}),
      })
    )) || { attempts: [] }
  )
}

export async function getSnapdragonJob({
  queryKey: [{ organizationId, jobId }],
  signal,
}: SnapdragonQueryContext<SnapdragonJobKeyEntries>): Promise<SnapdragonPrintJobResponse> {
  return axios.get<SnapdragonPrintJobResponse>(
    `/snapdragon/jobs/${encodeURIComponent(String(jobId))}`,
    buildAdminRequestConfig({
      organizationId,
      ...(signal ? { signal } : {}),
    })
  )
}

export async function requeueSnapdragonJob({
  organizationId,
  jobId,
}: SnapdragonJobTarget): Promise<SnapdragonPrintJobResponse> {
  return axios.post<SnapdragonPrintJobResponse>(
    `/snapdragon/jobs/${encodeURIComponent(String(jobId))}/requeue`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function markSnapdragonJobPrinted({
  organizationId,
  jobId,
}: SnapdragonJobTarget): Promise<SnapdragonPrintJobResponse> {
  return axios.post<SnapdragonPrintJobResponse>(
    `/snapdragon/jobs/${encodeURIComponent(String(jobId))}/mark-printed`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function cancelSnapdragonJob({
  organizationId,
  jobId,
}: SnapdragonJobTarget): Promise<SnapdragonPrintJobResponse> {
  return axios.post<SnapdragonPrintJobResponse>(
    `/snapdragon/jobs/${encodeURIComponent(String(jobId))}/cancel`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}

export async function dismissSnapdragonJob({
  organizationId,
  jobId,
}: SnapdragonJobTarget): Promise<SnapdragonDismissPrintJobResponse> {
  return axios.post<SnapdragonDismissPrintJobResponse>(
    `/snapdragon/jobs/${encodeURIComponent(String(jobId))}/dismiss`,
    null,
    buildAdminRequestConfig({ organizationId })
  )
}
